import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createPortal } from 'react-dom';
import { useState } from 'react';
import { formatMoeda } from '@/lib/formatMoeda';
import { CrmBoard, CrmBoardColumn, CrmNegocio } from '@/types/crm';
import { crmBoardsService } from '@/services/crmApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, Calendar, Clock, MoreVertical, Pencil, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { parseDateOnly } from '@/lib/dateOnly';
import { format } from 'date-fns';
import { diasSemAtualizar, DIAS_PARADO_AMBAR, DIAS_PARADO_VERMELHO } from '@/lib/crmNegocioStatus';
import { tagColor } from '@/lib/tagColor';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getIniciais } from '@/lib/avatarIniciais';
import { toast } from 'sonner';

// Mesma paleta fixa já usada como sugestão de cor em outros pontos do CRM (ex: tagColor.ts) —
// grade de botões clicáveis, não um seletor de cor livre.
const CORES_SUGERIDAS = ['#6b7280', '#3b82f6', '#a855f7', '#f59e0b', '#22c55e', '#ef4444', '#ec4899', '#14b8a6'];

const EMPTY_COL_FORM = { nome: '', cor: CORES_SUGERIDAS[0], is_ganho: false, is_perdido: false };

interface CrmBoardViewProps {
  board: CrmBoard;
  negocios: CrmNegocio[];
  onMove: (negocioId: string, columnId: string) => void;
  onCardClick: (negocio: CrmNegocio) => void;
  onAddCard: (columnId: string) => void;
  onColunasChange: () => void;
}


function DroppableColumn({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'min-h-[200px] flex-1 space-y-2 rounded-lg border border-dashed p-2 transition-colors',
        isOver ? 'border-primary bg-primary/5' : 'border-transparent',
      )}
    >
      {children}
    </div>
  );
}

function NegocioCard({ negocio, onClick }: { negocio: CrmNegocio; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: negocio.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const valorFormatado = formatMoeda(negocio.valor);
  const dias = diasSemAtualizar(negocio.updatedAt);
  const parado = dias !== null && dias >= DIAS_PARADO_AMBAR;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className="cursor-grab touch-none space-y-2 rounded-lg border bg-card p-3 text-sm shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing"
    >
      <p className="font-medium leading-tight">{negocio.titulo}</p>
      {negocio.conta && <p className="text-xs text-muted-foreground">{negocio.conta.razao_social}</p>}
      {valorFormatado && <p className="text-sm font-semibold text-primary">{valorFormatado}</p>}
      {negocio.tags && negocio.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {negocio.tags.map((tag) => (
            <Badge
              key={tag}
              variant="outline"
              className="px-1.5 py-0 text-[10px] font-normal"
              style={{ borderColor: tagColor(tag), color: tagColor(tag) }}
            >
              {tag}
            </Badge>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        {negocio.responsaveis && negocio.responsaveis.length > 0 && (
          <span className="flex items-center -space-x-1.5">
            {negocio.responsaveis.map((responsavel) => (
              <Tooltip key={responsavel.id}>
                <TooltipTrigger asChild>
                  <Avatar className="h-4 w-4 border border-background">
                    <AvatarImage src={responsavel.avatar_url ?? undefined} alt={responsavel.nome} />
                    <AvatarFallback className="text-[9px]">{getIniciais(responsavel.nome)}</AvatarFallback>
                  </Avatar>
                </TooltipTrigger>
                <TooltipContent>{responsavel.nome}</TooltipContent>
              </Tooltip>
            ))}
          </span>
        )}
        {negocio.data_previsao_fechamento && (
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {format(parseDateOnly(negocio.data_previsao_fechamento), 'dd/MM')}
          </span>
        )}
      </div>
      {negocio.createdAt && (
        <p className="text-[10px] text-muted-foreground/70">Criado em {format(new Date(negocio.createdAt), 'dd/MM/yyyy')}</p>
      )}
      {parado && (
        <span
          className={cn(
            'flex items-center gap-1 text-xs font-medium',
            dias! >= DIAS_PARADO_VERMELHO ? 'text-destructive' : 'text-amber-600 dark:text-amber-500',
          )}
        >
          <Clock className="h-3 w-3" /> sem atualização há {dias}d
        </span>
      )}
    </div>
  );
}

export function CrmBoardView({ board, negocios, onMove, onCardClick, onAddCard, onColunasChange }: CrmBoardViewProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const colunas = (board.colunas || []).slice().sort((a, b) => a.posicao - b.posicao);

  const [isNovaColunaOpen, setIsNovaColunaOpen] = useState(false);
  const [editColuna, setEditColuna] = useState<CrmBoardColumn | null>(null);
  const [colForm, setColForm] = useState(EMPTY_COL_FORM);
  const [deleteColumnId, setDeleteColumnId] = useState<string | null>(null);

  const negociosPorColuna = (columnId: string) =>
    negocios.filter((n) => n.column_id === columnId).sort((a, b) => a.posicao - b.posicao);

  const activeNegocio = activeId ? negocios.find((n) => n.id === activeId) : null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const negocio = negocios.find((n) => n.id === active.id);
    if (!negocio) return;

    const overIdStr = String(over.id);
    let novaColunaId: string | null = null;
    if (colunas.some((c) => c.id === overIdStr)) {
      novaColunaId = overIdStr;
    } else {
      const overNegocio = negocios.find((n) => n.id === overIdStr);
      if (overNegocio) novaColunaId = overNegocio.column_id;
    }

    if (novaColunaId && novaColunaId !== negocio.column_id) {
      onMove(negocio.id, novaColunaId);
    }
  };

  const openNovaColuna = () => {
    setColForm(EMPTY_COL_FORM);
    setIsNovaColunaOpen(true);
  };

  const handleCreateColuna = async () => {
    try {
      const result = await crmBoardsService.createColumn(board.id, colForm);
      if (result.success) {
        toast.success('Coluna criada');
        setIsNovaColunaOpen(false);
        onColunasChange();
      } else {
        toast.error(result.error || 'Erro ao criar coluna');
      }
    } catch {
      toast.error('Erro ao criar coluna');
    }
  };

  const openEditColuna = (coluna: CrmBoardColumn) => {
    setColForm({ nome: coluna.nome, cor: coluna.cor, is_ganho: coluna.is_ganho, is_perdido: coluna.is_perdido });
    setEditColuna(coluna);
  };

  const handleUpdateColuna = async () => {
    if (!editColuna) return;
    try {
      const result = await crmBoardsService.updateColumn(editColuna.id, colForm);
      if (result.success) {
        toast.success('Coluna atualizada');
        setEditColuna(null);
        onColunasChange();
      } else {
        toast.error(result.error || 'Erro ao atualizar coluna');
      }
    } catch {
      toast.error('Erro ao atualizar coluna');
    }
  };

  const handleMoveColuna = async (columnId: string, direction: 'up' | 'down') => {
    try {
      const result = await crmBoardsService.reorderColumn(columnId, direction);
      if (result.success) {
        onColunasChange();
      } else {
        toast.error(result.error || 'Não foi possível mover a coluna');
      }
    } catch {
      toast.error('Erro ao reordenar coluna');
    }
  };

  const handleDeleteColuna = async () => {
    if (!deleteColumnId) return;
    try {
      const result = await crmBoardsService.deleteColumn(deleteColumnId);
      if (result.success) {
        toast.success('Coluna excluída');
        onColunasChange();
      } else {
        toast.error(result.error || 'Erro ao excluir coluna');
      }
    } catch {
      toast.error('Erro ao excluir coluna');
    }
    setDeleteColumnId(null);
  };

  const renderColorPicker = () => (
    <div className="space-y-2">
      <Label>Cor</Label>
      <div className="flex flex-wrap gap-2">
        {CORES_SUGERIDAS.map((cor) => (
          <button
            key={cor}
            type="button"
            className={`h-7 w-7 rounded-full border-2 ${colForm.cor === cor ? 'border-foreground' : 'border-transparent'}`}
            style={{ backgroundColor: cor }}
            onClick={() => setColForm((f) => ({ ...f, cor }))}
          />
        ))}
      </div>
    </div>
  );

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4">
          {colunas.map((coluna: CrmBoardColumn, index) => {
            const negociosColuna = negociosPorColuna(coluna.id);
            const totalValor = negociosColuna.reduce((acc, n) => acc + (Number(n.valor) || 0), 0);
            return (
              <div key={coluna.id} className="flex w-72 flex-shrink-0 flex-col rounded-lg bg-muted/30 p-2">
                <div className="mb-2 flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: coluna.cor }} />
                    <span className="text-sm font-semibold">{coluna.nome}</span>
                    <span className="text-xs text-muted-foreground">{negociosColuna.length}</span>
                  </div>
                  <div className="flex items-center">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onAddCard(coluna.id)} title="Adicionar negócio">
                      <Plus className="h-4 w-4" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6" title="Opções da coluna">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditColuna(coluna)}>
                          <Pencil className="mr-2 h-4 w-4" /> Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled={index === 0} onClick={() => handleMoveColuna(coluna.id, 'up')}>
                          <ChevronLeft className="mr-2 h-4 w-4" /> Mover para esquerda
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled={index === colunas.length - 1} onClick={() => handleMoveColuna(coluna.id, 'down')}>
                          <ChevronRight className="mr-2 h-4 w-4" /> Mover para direita
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          disabled={colunas.length === 1}
                          onClick={() => setDeleteColumnId(coluna.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                {totalValor > 0 && (
                  <p className="mb-2 px-1 text-xs text-muted-foreground">{formatMoeda(totalValor)}</p>
                )}
                <SortableContext items={negociosColuna.map((n) => n.id)} strategy={verticalListSortingStrategy}>
                  <DroppableColumn id={coluna.id}>
                    {negociosColuna.map((negocio) => (
                      <NegocioCard key={negocio.id} negocio={negocio} onClick={() => onCardClick(negocio)} />
                    ))}
                  </DroppableColumn>
                </SortableContext>
              </div>
            );
          })}
          <div className="w-72 flex-shrink-0">
            <Button variant="outline" className="w-full border-dashed" onClick={openNovaColuna}>
              <Plus className="mr-2 h-4 w-4" />
              Nova coluna
            </Button>
          </div>
        </div>
        {createPortal(
          <DragOverlay>
            {activeNegocio ? (
              <div className="w-72 rounded-lg border bg-card p-3 text-sm shadow-lg">
                <p className="font-medium leading-tight">{activeNegocio.titulo}</p>
              </div>
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>

      <Dialog open={isNovaColunaOpen} onOpenChange={(open) => !open && setIsNovaColunaOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Coluna</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nova_col_nome">Nome</Label>
              <Input
                id="nova_col_nome"
                value={colForm.nome}
                onChange={(e) => setColForm((f) => ({ ...f, nome: e.target.value }))}
                placeholder="Ex: Contrato assinado"
              />
            </div>
            {renderColorPicker()}
            <div className="flex items-center gap-2">
              <Checkbox
                id="nova_col_ganho"
                checked={colForm.is_ganho}
                onCheckedChange={(v) => setColForm((f) => ({ ...f, is_ganho: !!v, is_perdido: v ? false : f.is_perdido }))}
              />
              <Label htmlFor="nova_col_ganho" className="font-normal">Marca o Negócio como Ganho ao entrar nesta coluna</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="nova_col_perdido"
                checked={colForm.is_perdido}
                onCheckedChange={(v) => setColForm((f) => ({ ...f, is_perdido: !!v, is_ganho: v ? false : f.is_ganho }))}
              />
              <Label htmlFor="nova_col_perdido" className="font-normal">Marca o Negócio como Perdido ao entrar nesta coluna</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNovaColunaOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateColuna} disabled={!colForm.nome.trim()}>
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editColuna} onOpenChange={(open) => !open && setEditColuna(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Coluna</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_col_nome">Nome</Label>
              <Input
                id="edit_col_nome"
                value={colForm.nome}
                onChange={(e) => setColForm((f) => ({ ...f, nome: e.target.value }))}
              />
            </div>
            {renderColorPicker()}
            <div className="flex items-center gap-2">
              <Checkbox
                id="edit_col_ganho"
                checked={colForm.is_ganho}
                onCheckedChange={(v) => setColForm((f) => ({ ...f, is_ganho: !!v, is_perdido: v ? false : f.is_perdido }))}
              />
              <Label htmlFor="edit_col_ganho" className="font-normal">Marca o Negócio como Ganho ao entrar nesta coluna</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="edit_col_perdido"
                checked={colForm.is_perdido}
                onCheckedChange={(v) => setColForm((f) => ({ ...f, is_perdido: !!v, is_ganho: v ? false : f.is_ganho }))}
              />
              <Label htmlFor="edit_col_perdido" className="font-normal">Marca o Negócio como Perdido ao entrar nesta coluna</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditColuna(null)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateColuna} disabled={!colForm.nome.trim()}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteColumnId} onOpenChange={() => setDeleteColumnId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão da coluna</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteColuna} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
